import { HttpErrorResponse } from '@angular/common/http';
import { computed } from '@angular/core';
import { UserDto } from '@api/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import * as Sentry from '@sentry/angular';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { Observable, pipe, switchMap, tap } from 'rxjs';

export interface CurrentUserService {
  getCurrentUser(): Observable<UserDto>;
}

export const requiresPasswordChange = (user: UserDto | undefined): boolean =>
  !!user?.forceToResetPassword && !user.ssoOnly;

export const useCurrentUser = () =>
  signalStoreFeature(
    {
      props: {
        _notificationService: type<NotificationService>(),
        _usersService: type<CurrentUserService>(),
      },
    },
    withState({
      currentUser: undefined as UserDto | undefined,
      currentUserLoaded: false,
    }),
    withComputed(store => {
      const isSuperAdmin = computed(() => store.currentUser()?.role === UserDto.RoleEnum.SuperAdmin);

      const is2FAEnabled = computed(() => !!store.currentUser()?.enabled2FA);

      const isSsoOnly = computed(() => !!store.currentUser()?.ssoOnly);

      return { isSuperAdmin, is2FAEnabled, isSsoOnly };
    }),
    withMethods(store => {
      const loadCurrentUser = rxMethod<void>(
        pipe(
          tap(() => patchState(store, { currentUserLoaded: false })),
          switchMap(() =>
            store._usersService.getCurrentUser().pipe(
              tapResponse({
                next: (currentUser: UserDto) => {
                  patchState(store, { currentUser, currentUserLoaded: true });
                  Sentry.setUser(currentUser.id ? { id: String(currentUser.id) } : null);
                },
                error: (error: HttpErrorResponse) => {
                  store._notificationService.showErrorNotification('Failed to load current user', error);
                  patchState(store, { currentUserLoaded: true });
                },
              }),
            ),
          ),
        ),
      );

      const setCurrentUser = (currentUser: UserDto) => patchState(store, { currentUser });

      return { loadCurrentUser, setCurrentUser };
    }),
  );
